<template>
  <layout-workflow no-top-bar>
    <v-container class="pb-0 px-10" fluid>
      <v-row no-gutters justify="space-between" class="pr-4">
        <v-col cols="auto" class="shrink">
          <trial-status-filters display-selected-filters @input="updateFilters($event)" />
        </v-col>
      </v-row>
    </v-container>
    <v-tabs v-model="currentTab" class="px-10" @change="onTabChange">
      <v-tab v-for="(tab, i) in tabs" :key="i" :data-e2e-id="`${tab.table}-tab`"
        >{{ tab.name }}
      </v-tab>
      <v-tabs-items v-model="currentTab">
        <v-tab-item
          v-for="(tab, i) in tabs"
          :key="i"
          :transition="'fade'"
          :reverse-transition="'fade'"
        >
          <insights-table
            class="pt-5"
            :selected-table="selectedTable"
            :selected-trial-id="selectedTrialId"
            :loading="fetchingTrialSummaries"
            :summaries="filteredSummaries"
            :options="options"
            :select-first-row="selectFirstRow"
            :generate-summary-display-name="generateSummaryDisplayName"
            @select-trial="onSelectTrial"
            @fetch-trial-summaries="onFetchTrialSummaries"
            @update:options="onUpdateOptions"
            @select-first-row="onSelectFirstRow"
            @edit-site-trial="onEditSiteTrial"
            @change-favorite="setFavorited"
          />
          <component
            :is="tab.component"
            :edited-site="editedSite"
            :summaries="filteredSummaries"
            :org-trial-external-id="orgTrialExternalId"
            :pre-selected-graph-type="selectedGraphType"
            :selected-table="selectedTable"
            :selected-trial-id="selectedTrialId"
            :selected-trial-name="selectedTrialName"
            :pre-selected-traces="selectedTraces"
            :trial-external-id="trialExternalId"
            @graph-type-change="onGraphTypeChange"
            @update-traces="onUpdateTraces"
          />
        </v-tab-item>
      </v-tabs-items>
    </v-tabs>
  </layout-workflow>
</template>

<script>
import DistributionTab from '@/components/insights/DistributionTab'
import EnrollmentTab from '@/components/insights/EnrollmentTab'
import InsightsTable from '@/components/insights/InsightsTable'
import LayoutWorkflow from '@/components/layout/LayoutWorkflow'
import PreScreeningTab from '@/components/insights/PreScreeningTab'
import SummaryTab from '@/components/insights/SummaryTab'
import { getPercentages } from '@/helpers/percentage-helper'
import { GraphTypes } from '@/helpers/graph-helper'
import { summaryValues } from '@/helpers/summary-value-helper'
import { DefaultSelectedTraces, TableTypes } from '@/helpers/table-helper'
import { MyActiveOrganization } from '@/helpers/my-account-helper'
import trialSummaryService from '@/services/trial-summary-service'
import { orgTrialFavoriteService } from '@/services/org-trial-favorite-service'
import _ from 'lodash'
import TrialStatusFilters from '@/components/trials/TrialStatusFilters'

export default {
  components: {
    TrialStatusFilters,
    DistributionTab,
    EnrollmentTab,
    InsightsTable,
    LayoutWorkflow,
    PreScreeningTab,
    SummaryTab
  },
  data() {
    return {
      currentTab: null,
      summaries: [],
      allSummaries: [],
      orgTrialFavorites: [],
      selectedGraphType: GraphTypes.STACKED_AREA,
      selectedTable: TableTypes.SUMMARY,
      selectedTrialId: null,
      fetchingTrialSummaries: true,
      options: {
        page: 1,
        sortBy: ['monthlyEnrollmentDifference'],
        sortDesc: [false],
        itemsPerPage: 5
      },
      selectFirstRow: true,
      selectedTraces: DefaultSelectedTraces[TableTypes.SUMMARY],
      editedSite: null,
      activeFilters: []
    }
  },
  computed: {
    filteredSummaries() {
      const summaries = _.cloneDeep(this.summaries)
      return _.filter(summaries, summary => {
        if (summary.children.length > 0) {
          summary.children = _.filter(summary.children, summary => this.meetsStatusFilter(summary))
          summary.children = _.orderBy(
            summary.children,
            this.options.sortBy,
            this.options.sortDesc[0] ? ['desc'] : ['asc']
          )
          return summary.children.length > 0
        } else {
          return this.meetsStatusFilter(summary)
        }
      })
    },
    tabs() {
      return [
        { name: TableTypes.SUMMARY, table: TableTypes.SUMMARY, component: 'SummaryTab' },
        { name: TableTypes.ENROLLMENT, table: TableTypes.ENROLLMENT, component: 'EnrollmentTab' },
        {
          name: `${TableTypes.PRE_SCREENING} Activity`,
          table: TableTypes.PRE_SCREENING,
          component: 'PreScreeningTab'
        },
        {
          name: TableTypes.DISTRIBUTION,
          table: TableTypes.DISTRIBUTION,
          component: 'DistributionTab'
        }
      ]
    },
    orgTrialExternalId() {
      const summary = _.find(this.summaries, it => it.trialId === this.selectedTrialId)
      return _.isNil(summary) ? this.selectedTrialId : summary.organizationExternalId
    },
    trialExternalId() {
      const summary = _.find(this.summaries, it => it.trialId === this.selectedTrialId)
      return _.isNil(summary) ? this.selectedTrialId : summary.trialExternalId
    },
    selectedTrialName() {
      const summary = _.find(this.allSummaries, it => it.trialId === this.selectedTrialId)

      if (_.isNil(summary)) return this.trialExternalId

      return this.generateSummaryDisplayName(summary)
    },
    trialSummaryParams() {
      return {
        summaryValues: [
          summaryValues.AVERAGE_DAYS_PRE_SCREENING,
          summaryValues.AVERAGE_DAYS_AWAITING_REVIEW,
          summaryValues.AVERAGE_DAYS_IN_REVIEW,
          summaryValues.AVERAGE_MONTHLY_ENROLLMENT,
          summaryValues.CURRENT_DURATION,
          summaryValues.TRIAL_FRIENDLY_NAME,
          summaryValues.MONTHLY_ENROLLMENT_DIFFERENCE,
          summaryValues.OVERALL_PARENT_TRIAL_ID,
          summaryValues.PATIENT_AWAITING_REVIEW_COUNT,
          summaryValues.PATIENT_COMPLETED_COUNT,
          summaryValues.PATIENT_FOLLOWING_COUNT,
          summaryValues.PATIENT_MATCHED_COUNT,
          summaryValues.PATIENT_COUNT,
          summaryValues.PATIENT_ENROLLED_COUNT,
          summaryValues.PATIENT_IN_REVIEW_COUNT,
          summaryValues.PATIENT_NOT_CONSENTED_COUNT,
          summaryValues.PATIENT_NOT_ELIGIBLE_COUNT,
          summaryValues.PLANNED_ENROLLMENT_TO_DATE,
          summaryValues.PROJECTED_TOTAL_ENROLLMENT,
          summaryValues.TARGET_DURATION,
          summaryValues.TARGET_MONTHLY_ENROLLMENT,
          summaryValues.TARGET_ENROLLMENT,
          summaryValues.TRIAL_CONTRACTED,
          summaryValues.TRIAL_EXTERNAL_ID,
          summaryValues.TRIAL_ID,
          summaryValues.TRIAL_LINK,
          summaryValues.TRIAL_SUMMARY_TYPE,
          summaryValues.TRIAL_PRIMARY_COMPLETION_DATE,
          summaryValues.ORGANIZATION_PARENT_TRIAL_EXTERNAL_ID,
          summaryValues.ORGANIZATION_RECRUITING_START_DATE,
          summaryValues.ORGANIZATION_RECRUITING_END_DATE,
          summaryValues.ORGANIZATION_EXTERNAL_ID,
          summaryValues.ORGANIZATION_ID,
          summaryValues.ORGANIZATION_NAME,
          summaryValues.ORGANIZATION_TRIAL_CARE_TEAM,
          summaryValues.ORGANIZATION_TRIAL_ID,
          summaryValues.ORGANIZATION_TRIAL_STATUS
        ]
      }
    }
  },
  created() {
    this.onFetchTrialSummaries()
  },
  methods: {
    onSelectTrial(trialId) {
      this.selectedTrialId = trialId
    },
    onGraphTypeChange(graphType) {
      this.selectedGraphType = graphType
    },
    onTabChange(tab) {
      this.selectedTable = this.tabs[tab].table
      this.selectedTraces = DefaultSelectedTraces[this.selectedTable]
    },
    async onFetchTrialSummaries() {
      this.fetchingTrialSummaries = true
      try {
        const favoritesResponse = await orgTrialFavoriteService.get(MyActiveOrganization.id)
        this.orgTrialFavorites = favoritesResponse.data || []
        let { data } = await trialSummaryService.listTrialSummaries(this.trialSummaryParams)
        this.allSummaries = data
        const [topLevelSummaries, nestedSummaries] = _.partition(data, summary =>
          _.isNil(summary.overallParentTrialId)
        )
        let topLevelSummariesWithPercentages = _.map(topLevelSummaries, summary => {
          summary.favorite = this.isFavorited(summary.organizationTrialId)
          summary.children = []
          return this.addPercentages(summary)
        })
        _.forEach(nestedSummaries, nested => {
          nested.favorite = this.isFavorited(nested.organizationTrialId)
          const summaryWithPercentage = this.addPercentages(nested)
          const parent = topLevelSummariesWithPercentages.find(
            s => s.trialId === nested.overallParentTrialId
          )
          parent.children.push(summaryWithPercentage)
        })
        this.summaries = topLevelSummariesWithPercentages
      } finally {
        this.fetchingTrialSummaries = false
      }
    },
    async setFavorited(summary) {
      await orgTrialFavoriteService.setFavorited(
        MyActiveOrganization.id,
        summary.organizationTrialId,
        !summary.favorite
      )
      summary.favorite = !summary.favorite
    },
    isFavorited(orgTrialId) {
      const favorite =
        _.find(this.orgTrialFavorites, it => {
          return it && it.organizationTrial && it.organizationTrial.id === orgTrialId
        }) || {}
      return favorite.favorited
    },
    addPercentages(summary) {
      const completeStatusPercents = getPercentages([
        summary.patientEnrolledCount,
        summary.patientNotConsentedCount,
        summary.patientNotEligibleCount
      ])
      const identifiedPercents = getPercentages([
        summary.patientAwaitingReviewCount,
        summary.patientInReviewCount,
        summary.patientCompletedCount
      ])
      return _.merge({}, summary, {
        patientEnrolledPercent: completeStatusPercents[0],
        patientNotConsentedPercent: completeStatusPercents[1],
        patientNotEligiblePercent: completeStatusPercents[2],
        patientAwaitingReviewIdentifiedPercent: identifiedPercents[0],
        patientInReviewIdentifiedPercent: identifiedPercents[1],
        patientCompletedIdentifiedPercent: identifiedPercents[2]
      })
    },
    onUpdateOptions(options) {
      this.options = options
    },
    onSelectFirstRow(shouldSelectFirstRow) {
      this.selectFirstRow = shouldSelectFirstRow
    },
    onUpdateTraces(traces) {
      this.selectedTraces = traces
    },
    onEditSiteTrial(site) {
      this.editedSite = site
    },
    updateFilters(filters) {
      this.activeFilters = filters
    },
    meetsStatusFilter(summary) {
      return (
        _.isEmpty(this.activeFilters) ||
        _.isNil(summary.organizationTrialStatus) ||
        _.includes(this.activeFilters, summary.organizationTrialStatus)
      )
    },
    generateSummaryDisplayName(summary) {
      if (summary.summaryType === 'TRIAL' && summary.organizationExternalId)
        return summary.organizationExternalId

      if (summary.summaryType === 'PARENT' && summary.organizationParentTrialExternalId)
        return summary.organizationParentTrialExternalId

      return summary.trialFriendlyName || summary.trialExternalId
    }
  }
}
</script>

<style lang="scss" scoped></style>
